import React, { useEffect, useMemo, useState, useRef } from "react";
import { Radio, Select, Skeleton, Button } from "antd";
import { BsSortDown, BsSortDownAlt } from "react-icons/bs";
import Delta from "./Delta";
import { sortBy } from "underscore";
import { titleCase } from "@ts/utils";
import { getRevstackCardTableData } from "@ts/retrieve-data";
import { ALL_OPTIONS, COMPONENT_FILTER_QUERY_CONFIG } from "@ts/constants";
import { showToast } from "@ts/modals";
import { useSelector } from "react-redux";
import { drilldownClickHandler } from "@ts/revstack/constants";

const formatCardMetric = (value: any, unit = ""): string => {
    if (!value) {
        return "-";
    }
    
    const numericValue = Number(value);
    if (!isNaN(numericValue)) {
        return `${Math.abs(numericValue).toFixed(1)}${unit}`;
    }
    
    return "-";
};

interface IPerformanceCardWidgetProps {
    widgetConfig: any;
    cardData: any[];
    setSelectedCardName: (name: string, id?: string) => void;
    type?: "subcategory" | "regional";
    isTopCardRequired?: boolean;
    topCardData?: any;
    isLoading?: boolean;
    relatedComponentDataKeys: string[];
    configSessionKey?: string;
}

interface ICardItemProps {
    widgetConfig: any;
    data: any;
    index: number;
    selectedCardId: string;
    type: any;
    isTopCard?: boolean;
}

const CardItem: React.FC<ICardItemProps> = React.memo(({
    widgetConfig,
    data,
    index,
    selectedCardId,
    type,
    isTopCard = false,
}) => {
    const value = isTopCard ? data?.id : data?.[widgetConfig?.card?.cardId];
    return (
        <Radio
            key={index}
            value={value}
            className="performance-radio-card my-1 w-100"
        >
            <div
                role="button"
                className="d-flex align-items-center category-item w-100 h-100"
                style={{
                    padding: "10px 10px 10px 6px",
                    borderRadius: "6px",
                    border: "1.5px solid",
                    borderColor:
                        selectedCardId === value ? "#0078AA" : "#CBD9E7",
                    backgroundColor:
                        selectedCardId === value ? "#F0F4F8" : "#fff",
                    transition: "all 0.2s ease",
                }}
            >
                <div
                    style={{
                        width: 8,
                        height: 80,
                        backgroundColor: data?.color,
                    }}
                ></div>
                <div style={{ marginLeft: 12, width: "100%" }}>
                    <div
                        style={{
                            fontSize: "14px",
                            color: "#1a1f36",
                            fontWeight: 600,
                        }}
                    >
                        {isTopCard ? data?.name?.toUpperCase() : data?.name}
                    </div>

                    <div
                        className="d-flex align-items-center justify-content-between w-100"
                        style={{ marginTop: "8px" }}
                    >
                        <span
                            style={{
                                fontSize: "12px",
                                color: "#919191",
                            }}
                        >
                            {widgetConfig?.card?.primaryMetricLabel}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                            <span
                                style={{
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    color: "#1a1f36",
                                }}
                            >
                                {formatCardMetric(
                                    data?.[widgetConfig?.card?.primaryMetricKey]?.value,
                                    widgetConfig?.card?.primaryMetricUnit
                                )}
                            </span>
                            <span
                                className={`d-flex align-items-center justify-content-end gap-1 ${
                                    data[widgetConfig?.card?.primaryMetricKey]
                                        ?.trend?.isPositive
                                        ? "text-success"
                                        : "text-danger"
                                }`}
                                style={{ fontSize: "14px" }}
                            >
                                {
                                    <span className="d-flex align-items-center justify-content-end gap-1">
                                        { typeof data[widgetConfig?.card?.primaryMetricKey]?.trend?.isPositive === "boolean" && <Delta
                                            isPositive={
                                                data[
                                                    widgetConfig?.card
                                                        ?.primaryMetricKey
                                                ]?.trend?.isPositive
                                            }
                                        />}
                                        <span>
                                            {formatCardMetric(
                                                data?.[widgetConfig?.card?.primaryMetricKey]?.trend?.value,
                                                widgetConfig?.card?.primaryMetricUnit
                                            )}
                                        </span>
                                    </span>
                                }
                            </span>
                        </div>
                    </div>

                    {data?.[widgetConfig?.card?.secondaryMetricKey] && (
                        <div
                            className="d-flex align-items-center justify-content-between w-100"
                            style={{ marginTop: "4px" }}
                        >
                            <span
                                style={{
                                    fontSize: "12px",
                                    color: "#919191",
                                }}
                            >
                                {widgetConfig?.card?.secondaryMetricLabel}
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                <span
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#1a1f36",
                                    }}
                                >
                                    {formatCardMetric(
                                        data?.[widgetConfig?.card?.secondaryMetricKey]?.value,
                                        widgetConfig?.card?.secondaryMetricUnit
                                    )}
                                </span>
                                <span
                                    className={`d-flex align-items-center justify-content-end gap-1 ${
                                        data?.[
                                            widgetConfig?.card
                                                ?.secondaryMetricKey
                                        ]?.trend?.isPositive
                                            ? "text-success"
                                            : "text-danger"
                                    }`}
                                    style={{ fontSize: "12px" }}
                                >
                                    {data?.[
                                        widgetConfig?.card?.secondaryMetricKey
                                    ]?.trend?.value !== undefined &&
                                    data?.[
                                        widgetConfig?.card?.secondaryMetricKey
                                    ]?.trend?.value !== null ? (
                                        <span className="d-flex align-items-center justify-content-end gap-1">
                                            {typeof data?.[widgetConfig?.card?.secondaryMetricKey]?.trend?.isPositive === "boolean" && <Delta
                                                isPositive={
                                                    data?.[
                                                        widgetConfig?.card
                                                            ?.secondaryMetricKey
                                                    ]?.trend?.isPositive
                                                }
                                            />}
                                            <span>
                                                {formatCardMetric(
                                                    data?.[widgetConfig?.card?.secondaryMetricKey]?.trend?.value,
                                                    widgetConfig?.card?.secondaryMetricUnit
                                                )}
                                            </span>
                                        </span>
                                    ) : (
                                        "-"
                                    )}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Radio>
    );
});

const PerformanceCardWidget: React.FC<IPerformanceCardWidgetProps> = ({
    widgetConfig,
    cardData,
    type = "subcategory",
    isTopCardRequired = true,
    topCardData,
    setSelectedCardName,
    isLoading = false,
    relatedComponentDataKeys,
    configSessionKey = "dashboard-config",
}) => {
    const drilldownFilters = useSelector(
        (state: any) => state.drilldownFilters
    );

    const [selectedCardId, setSelectedCardId] = useState<string>(
        topCardData?.id || cardData?.[0]?.id
    );

    const [selectedFilter, setSelectedFilter] = useState<string>(
        widgetConfig?.card?.primaryMetricKey
    );
    const [isAscending, setIsAscending] = useState<boolean>(true);
    const isRequestInProgressRef = useRef<boolean>(false);
    const hasInitialLoadRun = useRef<boolean>(false);
    const sortedCardData = useMemo(() => {
        if (!widgetConfig?.card?.isSortFilterRequired) return cardData;

        const filteredData = cardData;

        if (!filteredData || filteredData.length === 0) return filteredData;

        const sortedData = sortBy(filteredData, (item) => {
            if (selectedFilter === widgetConfig?.card?.primaryMetricKey) {
                return item[widgetConfig?.card?.primaryMetricKey]?.value || 0;
            } else if (
                selectedFilter === widgetConfig?.card?.secondaryMetricKey
            ) {
                return item[widgetConfig?.card?.secondaryMetricKey]?.value || 0;
            }
            return 0;
        });

        return isAscending ? sortedData : sortedData.reverse();
    }, [cardData, selectedFilter, type, isAscending]);

    const filterOptions = [
        {
            label: "Sort By " + widgetConfig?.card?.primaryMetricLabel,
            value: widgetConfig?.card?.primaryMetricKey,
        },
        {
            label: "Sort By " + widgetConfig?.card?.secondaryMetricLabel,
            value: widgetConfig?.card?.secondaryMetricKey,
        },
    ];

    // Helper function to compute and update drilldown filters from card data
    const updateCardFilters = async (cardId: string) => {
        if (allCardsData.length === 0) return;

        const cardIdKey = widgetConfig?.card?.cardId || 'id';
        const filterKey = widgetConfig.card.type || type;
        const isSelectedCardIdNumeric = !isNaN(Number(cardId));
        
        const options = allCardsData.map((item: any) => ({
            label: titleCase(item.name),
            value: item?.id ? item?.id : +(item?.[cardIdKey] || item?.id),
        }));
        
        const selectedValue = isSelectedCardIdNumeric ? +cardId : cardId;
        const filterValue = selectedValue === "all" ? ["all"] : [selectedValue];
        
        // Get title from selected card
        const selectedCard = allCardsData.find((item: any) => 
            (item?.id ? item?.id : item?.[cardIdKey]) === selectedValue
        );
        const cardTitle = selectedCard ? (selectedCard.name || selectedCard.id) : "";
        
        // Use primaryFilters with overwriteParams to update filters
        await drilldownClickHandler({
            primaryFilters: {
                filterKey: filterKey,
                overwriteParams: {
                    options: options,
                    filterValue: filterValue,
                    title: cardTitle,
                }
            },
            secondaryFilters: {},
            record: {},
            skipStateUpdate: true, // Don't open drilldown, just update filters
        });
    };

    const handleCardSelect = async (id: string) => {
        if (isRequestInProgressRef.current) {
            return;
        }

        // Prevent unnecessary API calls if the same card is already selected
        if (selectedCardId === id) {
            return;
        }
        setSelectedCardId(id);
        
        // Update drilldown filters based on card selection
        await updateCardFilters(id);

        isRequestInProgressRef.current = true;

        const sessionWidgetConfig = JSON.parse(
            sessionStorage.getItem(configSessionKey)
        );

        const sectionData = sessionWidgetConfig.find(
            (item: any) =>
                item.sectionKey ===
                COMPONENT_FILTER_QUERY_CONFIG[widgetConfig?.card?.cardType]
                    .sectionKey
        );

        if (!sectionData) {
            showToast("danger", `Failed to get ${type} data`);
            isRequestInProgressRef.current = false;
            return;
        }

        try {
            await getRevstackCardTableData(
                sectionData,
                widgetConfig?.card?.cardType,
                id.toString(),
                relatedComponentDataKeys
            );
        } catch (error: any) {
            console.error("Error fetching card data:", error);
        } finally {
            // Mark request as completed
            isRequestInProgressRef.current = false;
        }
    };

    const handleSortToggle = () => {
        setIsAscending(!isAscending);
    };

    const allCardsData = useMemo(() => {
        return [topCardData, ...cardData];
    }, [topCardData, cardData]);

    useEffect(() => {
        const initialCardId = topCardData?.id || cardData?.[0]?.id;

        if (!selectedCardId && initialCardId) {
            setSelectedCardId(initialCardId);
        }
            if (
                initialCardId &&
            allCardsData.length > 0 &&
            !hasInitialLoadRun.current &&
            widgetConfig?.card?.isSortFilterRequired
        ) {
            if(initialCardId !== topCardData?.id){
                handleCardSelect(initialCardId);
                hasInitialLoadRun.current = true;
            }else{
                // Update filters without triggering data fetch
                updateCardFilters(initialCardId);
                hasInitialLoadRun.current = true;
            }
        } else {
            if(initialCardId !== topCardData?.id){
                handleCardSelect(initialCardId);
            }
        }

        if (selectedCardId && allCardsData.length > 0) {
            const selectedCard = allCardsData.find(
                (item: any) =>
                    item.id === selectedCardId ||
                    item[widgetConfig?.card?.cardId] === selectedCardId
            );
            if (selectedCard) {
                setSelectedCardName(selectedCard.name, selectedCardId);
            }

        }

    }, [
        topCardData?.id,
        cardData,
        selectedCardId,
        widgetConfig?.card?.cardId,
        allCardsData.length,
    ]);

    if (isLoading) {
        return (
            <Skeleton.Node className="w-100" active style={{ height: 455 }} />
        );
    }

    return (
        <div className="d-flex flex-column gap-2 w-100">
            {widgetConfig?.card?.isSortFilterRequired && (
                <div className="d-flex align-items-center justify-content-between w-100 gap-2">
                    <Select
                        className="w-100"
                        options={filterOptions}
                        value={selectedFilter}
                        onChange={(value) => setSelectedFilter(value)}
                        style={{
                            height: 35,
                        }}
                    />

                    <Button
                        type="default"
                        size="small"
                        onClick={handleSortToggle}
                        className="d-flex align-items-center justify-content-center"
                        style={{
                            width: 35,
                            height: 35,
                            padding: 0,
                            backgroundColor: "#F0F4F8",
                            borderRadius: "6px",
                            flexShrink: 0,
                        }}
                        icon={
                            isAscending ? (
                                <BsSortDownAlt size={16} />
                            ) : (
                                <BsSortDown size={16} />
                            )
                        }
                    />
                </div>
            )}
            <Radio.Group
                className="w-100"
                value={selectedCardId}
                onChange={(e) => handleCardSelect(e.target.value)}
            >
                {isTopCardRequired && topCardData && (
                    <CardItem
                        widgetConfig={widgetConfig}
                        data={topCardData}
                        index={0}
                        selectedCardId={selectedCardId}
                        type={type}
                        isTopCard={true}
                    />
                )}

                {sortedCardData.map((data, index) => (
                    <CardItem
                        widgetConfig={widgetConfig}
                        key={index}
                        data={data}
                        index={isTopCardRequired ? index + 1 : index}
                        selectedCardId={selectedCardId}
                        type={type}
                    />
                ))}
            </Radio.Group>
        </div>
    );
};

export default PerformanceCardWidget;